import { isFemale } from '@/flow/conditions'
import {
  sharedActivityFrequencyStep,
  sharedWaterIntakeStep,
} from '@/flow/shared/commonQuestions'
import type { FlowContext, Step } from '@/types'

const hasConcern = (concern: string) => (ctx: FlowContext) => {
  const concerns = ctx.vars.preocupacoes_lipedema
  return Array.isArray(concerns) && concerns.includes(concern)
}

const hasCurrentTreatment = (ctx: FlowContext) => {
  const treatments = ctx.vars.tratamentos_lipedema
  return Array.isArray(treatments) && treatments.length > 0
}

const hasDiagnosisOrSuspicion = (ctx: FlowContext) =>
  ['diagnosticado', 'suspeita'].includes(ctx.vars.diagnostico_lipedema)

/** Objective-specific steps: Lipedema */
export const lipedemaSteps: Step[] = [
  // ========== INTRO ==========
  {
    id: 'intro_lipedema',
    type: 'message',
    content:
      'Vamos entender seus sintomas e histórico para montar um protocolo mais direcionado para lipedema.',
  },

  // ========== MAIN SYMPTOMS ==========
  {
    id: 'lipedema_areas',
    type: 'choice',
    inputKey: 'areas_lipedema',
    multiple: true,
    minSelections: 1,
    content: 'Em quais regiões você percebe mais acometimento?',
    options: [
      { label: 'Quadril / Culote', value: 'quadril_culote' },
      { label: 'Coxas', value: 'coxas' },
      { label: 'Joelhos', value: 'joelhos' },
      { label: 'Panturrilhas', value: 'panturrilhas' },
      { label: 'Tornozelos', value: 'tornozelos' },
      { label: 'Braços', value: 'bracos' },
    ],
  },
  {
    id: 'lipedema_concerns',
    type: 'choice',
    inputKey: 'preocupacoes_lipedema',
    multiple: true,
    minSelections: 1,
    content: 'O que mais te incomoda hoje em relação ao lipedema?',
    options: [
      { label: 'Dor ao toque / sensibilidade / desconforto', value: 'dor_sensibilidade' },
      { label: 'Sensação de peso nas pernas ou braços', value: 'peso' },
      { label: 'Inchaço ao longo do dia', value: 'inchaco' },
      { label: 'Gordura desproporcional nas áreas afetadas', value: 'gordura_desproporcional' },
      { label: 'Dificuldade para reduzir medidas mesmo emagrecendo', value: 'dificuldade_medidas' },
      { label: 'Hematomas com facilidade', value: 'hematomas' },
      { label: 'Nódulos / irregularidades sob a pele', value: 'nodulos' },
    ],
  },
  {
    id: 'lipedema_pain_intensity',
    type: 'choice',
    inputKey: 'dor_lipedema_intensidade',
    content: 'Como você classificaria a dor ou sensibilidade nas áreas afetadas?',
    condition: hasConcern('dor_sensibilidade'),
    options: [
      { label: 'Leve', value: 'leve' },
      { label: 'Moderada', value: 'moderada' },
      { label: 'Intensa', value: 'intensa' },
      { label: 'Oscila bastante', value: 'oscilante' },
    ],
  },
  {
    id: 'lipedema_swelling_pattern',
    type: 'choice',
    inputKey: 'inchaco_lipedema',
    content: 'Como costuma ser o inchaço?',
    condition: hasConcern('inchaco'),
    options: [
      { label: 'Piora ao longo do dia', value: 'piora_dia' },
      { label: 'É constante', value: 'constante' },
      { label: 'Piora no calor ou ao ficar muito tempo em pé', value: 'calor_ortostatismo' },
      { label: 'Aparece só em alguns períodos', value: 'episodico' },
    ],
  },
  {
    id: 'lipedema_bruise_frequency',
    type: 'choice',
    inputKey: 'hematomas_frequencia',
    content: 'Com que frequência você percebe hematomas fáceis?',
    condition: hasConcern('hematomas'),
    options: [
      { label: 'Quase sempre', value: 'quase_sempre' },
      { label: 'Algumas vezes por mês', value: 'algumas_vezes_mes' },
      { label: 'Raramente', value: 'raramente' },
    ],
  },
  {
    id: 'lipedema_routine_impact',
    type: 'choice',
    inputKey: 'impacto_rotina_lipedema',
    content: 'Quanto isso impacta sua rotina hoje?',
    options: [
      { label: 'Pouco', value: 'pouco' },
      { label: 'Moderadamente', value: 'moderado' },
      { label: 'Bastante', value: 'alto' },
      { label: 'Muito, limita minhas atividades', value: 'muito_alto' },
    ],
  },

  // ========== HISTORY ==========
  {
    id: 'intro_lipedema_history',
    type: 'message',
    content: 'Agora vamos falar do seu histórico e do que pode ter influenciado a evolução do quadro.',
  },
  {
    id: 'lipedema_diagnosis_status',
    type: 'choice',
    inputKey: 'diagnostico_lipedema',
    content: 'Você já recebeu diagnóstico de lipedema?',
    options: [
      { label: 'Sim, por profissional de saúde', value: 'diagnosticado' },
      { label: 'Tenho suspeita, mas sem confirmação', value: 'suspeita' },
      { label: 'Nunca investiguei', value: 'nao_investiguei' },
    ],
  },
  {
    id: 'lipedema_stage',
    type: 'choice',
    inputKey: 'estagio_lipedema',
    content: 'Você sabe o estágio ou a gravidade percebida do lipedema?',
    condition: hasDiagnosisOrSuspicion,
    options: [
      { label: 'Estágio inicial / leve', value: 'leve' },
      { label: 'Estágio intermediário', value: 'moderado' },
      { label: 'Estágio avançado', value: 'avancado' },
      { label: 'Não sei informar', value: 'nao_sei' },
    ],
  },
  {
    id: 'lipedema_onset_female',
    type: 'choice',
    inputKey: 'inicio_lipedema',
    content: 'Quando você percebeu o início ou piora dos sintomas?',
    condition: isFemale,
    options: [
      { label: 'Desde a adolescência / puberdade', value: 'puberdade' },
      { label: 'Após gestação / pós-parto', value: 'gestacao' },
      { label: 'Após anticoncepcional ou outra mudança hormonal', value: 'hormonal' },
      { label: 'Na peri ou pós-menopausa', value: 'menopausa' },
      { label: 'Nos últimos 1-2 anos', value: 'ultimos_anos' },
      { label: 'Sempre tive essa tendência', value: 'sempre' },
      { label: 'Não sei ao certo', value: 'nao_sei' },
    ],
  },
  {
    id: 'lipedema_onset_other',
    type: 'choice',
    inputKey: 'inicio_lipedema',
    content: 'Quando você percebeu o início ou piora dos sintomas?',
    condition: (ctx) => !isFemale(ctx),
    options: [
      { label: 'Desde a adolescência', value: 'adolescencia' },
      { label: 'Após ganho de peso importante', value: 'ganho_peso' },
      { label: 'Após uso de hormônios ou medicação', value: 'hormonal' },
      { label: 'Nos últimos 1-2 anos', value: 'ultimos_anos' },
      { label: 'Sempre tive essa tendência', value: 'sempre' },
      { label: 'Não sei ao certo', value: 'nao_sei' },
    ],
  },
  {
    id: 'lipedema_progression',
    type: 'choice',
    inputKey: 'progressao_lipedema',
    content: 'Como você percebe a evolução do quadro?',
    options: [
      { label: 'Está estável há bastante tempo', value: 'estavel' },
      { label: 'Piora lentamente com o tempo', value: 'progressiva_lenta' },
      { label: 'Piorou rápido nos últimos meses', value: 'progressiva_rapida' },
      { label: 'Oscila conforme peso, hormônios ou rotina', value: 'oscilante' },
    ],
  },
  {
    id: 'lipedema_family_history',
    type: 'choice',
    inputKey: 'historico_familiar_lipedema',
    content: 'Existe histórico parecido na sua família?',
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Não', value: 'nao' },
      { label: 'Não sei', value: 'nao_sei' },
    ],
  },
  {
    id: 'lipedema_pregnancy_warning',
    type: 'message',
    content:
      'Durante a gestação, não prescrevemos protocolo medicamentoso automático. Seu caso precisa de avaliação individual com seu obstetra.',
    condition: (ctx) => ctx.vars.gestante_amamentando === 'gestante',
  },

  // ========== CURRENT TREATMENTS ==========
  {
    id: 'lipedema_current_treatments',
    type: 'choice',
    inputKey: 'tratamentos_lipedema',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Ainda não faço tratamento',
    content: 'O que você já faz hoje para controlar os sintomas?',
    options: [
      { label: 'Meia de compressão', value: 'compressao' },
      { label: 'Drenagem linfática', value: 'drenagem' },
      { label: 'Fisioterapia / terapia descongestiva', value: 'fisioterapia' },
      { label: 'Exercícios de baixo impacto ou na água', value: 'exercicio_baixo_impacto' },
      { label: 'Alimentação anti-inflamatória', value: 'alimentacao' },
      { label: 'Suplementos / fitoterápicos', value: 'suplementos' },
      { label: 'Medicação para dor ou inchaço', value: 'medicacao' },
      { label: 'Acompanhamento médico regular', value: 'acompanhamento_medico' },
    ],
  },
  {
    id: 'lipedema_treatment_duration',
    type: 'choice',
    inputKey: 'tratamento_lipedema_duracao',
    content: 'Há quanto tempo você segue esse cuidado ou tratamento?',
    condition: hasCurrentTreatment,
    options: [
      { label: 'Menos de 3 meses', value: 'menos_3_meses' },
      { label: '3 a 6 meses', value: '3_6_meses' },
      { label: '6 meses a 1 ano', value: '6_meses_1_ano' },
      { label: 'Mais de 1 ano', value: 'mais_1_ano' },
    ],
  },
  {
    id: 'lipedema_treatment_results',
    type: 'choice',
    inputKey: 'tratamento_lipedema_resultado',
    content: 'Como você avalia os resultados até agora?',
    condition: hasCurrentTreatment,
    options: [
      { label: 'Melhorou bastante', value: 'melhorou' },
      { label: 'Melhorou um pouco', value: 'melhorou_pouco' },
      { label: 'Não percebi diferença', value: 'sem_diferenca' },
      { label: 'Piorou ou não consegui manter', value: 'piorou' },
    ],
  },

  // ========== LIFESTYLE ==========
  {
    id: 'intro_lipedema_lifestyle',
    type: 'message',
    content: 'Por fim, algumas perguntas rápidas sobre hábitos que também influenciam os sintomas.',
  },
  { ...sharedActivityFrequencyStep },
  { ...sharedWaterIntakeStep },
  {
    id: 'lipedema_health_conditions_female',
    type: 'choice',
    inputKey: 'condicoes_associadas_lipedema',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Nenhuma dessas',
    content: 'Você tem alguma dessas condições associadas?',
    condition: isFemale,
    options: [
      { label: 'Varizes / insuficiência venosa', value: 'varizes' },
      { label: 'Linfedema', value: 'linfedema' },
      { label: 'Hipotireoidismo / tireoide', value: 'tireoide' },
      { label: 'SOP', value: 'sop' },
      { label: 'Endometriose', value: 'endometriose' },
      { label: 'Ganho de peso importante / obesidade', value: 'ganho_peso' },
      { label: 'Dores articulares', value: 'dores_articulares' },
    ],
  },
  {
    id: 'lipedema_health_conditions_other',
    type: 'choice',
    inputKey: 'condicoes_associadas_lipedema',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Nenhuma dessas',
    content: 'Você tem alguma dessas condições associadas?',
    condition: (ctx) => !isFemale(ctx),
    options: [
      { label: 'Varizes / insuficiência venosa', value: 'varizes' },
      { label: 'Linfedema', value: 'linfedema' },
      { label: 'Hipotireoidismo / tireoide', value: 'tireoide' },
      { label: 'Ganho de peso importante / obesidade', value: 'ganho_peso' },
      { label: 'Dores articulares', value: 'dores_articulares' },
    ],
  },

  // ========== GOALS ==========
  {
    id: 'lipedema_goals',
    type: 'choice',
    inputKey: 'objetivos_lipedema',
    multiple: true,
    minSelections: 1,
    content: 'Quais são seus principais objetivos agora?',
    options: [
      { label: 'Reduzir dor e sensibilidade', value: 'reduzir_dor' },
      { label: 'Diminuir inchaço', value: 'reduzir_inchaco' },
      { label: 'Aliviar sensação de peso', value: 'aliviar_peso' },
      { label: 'Melhorar mobilidade e disposição', value: 'mobilidade' },
      { label: 'Controlar a progressão do quadro', value: 'controlar_progressao' },
      { label: 'Melhorar contorno corporal', value: 'contorno_corporal' },
      { label: 'Melhorar autoestima e conforto no dia a dia', value: 'autoestima' },
    ],
  },
]
